/**
 * SettlementTimelineChart — "Six tenders. Six arrival times."
 * A settlement timeline: one Friday-8pm sale fans out into different
 * arrival times and shapes by tender type. Non-linear axis (Fri 8pm →
 * Wed, axis break, ~2 weeks). Markers carry hover/tap tooltips.
 * Requires React 16.8+. No props; injects scoped styles once.
 */
'use client';

import React, { useEffect } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Data --------------------------------- */
interface Row { name: string; sub?: string; u: number; range?: [number, number]; arrive: string; arrive2?: string; form: string; chip: 'GROSS' | 'NET' | 'CONTRACT'; color: string; hollow?: boolean; square?: boolean; tip: string; }
const ROWS: Row[] = [
  { name: 'FPS', u: 0.08, arrive: 'Seconds · 24/7', form: 'Individual credits', chip: 'GROSS', color: '#0f9993', tip: '<b>FPS</b><br>One credit per payment, in seconds —<br>runs 24/7 including weekends. <span class="t-val">HKICL</span>' },
  { name: 'Cash', u: 0.3, arrive: 'Same night — the safe', form: 'banked when banked', chip: 'GROSS', color: '#3E5460', hollow: true, tip: '<b>Cash</b><br>In the safe Friday night; reaches the bank<br>whenever someone takes it there.' },
  { name: 'Cards (Visa/MC)', u: 3, range: [3, 5], arrive: 'Mon', arrive2: 'Wed', form: 'Daily batch', chip: 'NET', color: '#0f9993', tip: '<b>Cards (Visa / Mastercard)</b><br>Next business day, Mon–Fri only — Fri/Sat/Sun<br>sales roll to Monday. Net of MDR.<br><span class="t-val">T+1–T+3 varies by acquirer · Hang Seng</span>' },
  { name: 'Octopus', u: 3, arrive: 'Monday', form: 'One batch · T+1 from upload', chip: 'CONTRACT', color: '#0f9993', tip: '<b>Octopus</b><br>T+1 business day after data upload —<br>weekend sales land Monday-ish.<br><span class="t-val">Fee mechanics contract-based · Octopus FAQ</span>' },
  { name: 'WeChat Pay HK', u: 4, range: [4, 5.4], arrive: 'Tue', arrive2: 'Thu–Fri', form: 'One batch', chip: 'NET', color: '#0f9993', tip: '<b>WeChat Pay HK</b><br>T+2 via bank acquirer; refunds deducted.<br><span class="t-val">Direct route 2–5 days · BOCHK schedule</span>' },
  { name: 'AlipayHK', u: 4, range: [4, 5], arrive: 'Tue', arrive2: 'Wed', form: 'One batch', chip: 'NET', color: '#0f9993', tip: '<b>AlipayHK</b><br>Settled within 1 working day; typically lands<br>in 2–3. Net of fees, refunds, chargebacks.<br><span class="t-val">AlipayHK FAQ · BOCHK schedule</span>' },
  { name: 'Delivery platforms', sub: 'KeeTa · foodpanda', u: 6.45, arrive: '~2 weeks', form: 'Lump sum · ~2×/mo', chip: 'NET', color: '#B08A2E', square: true, tip: '<b>Delivery platforms</b><br>Periodic lump sum, ~twice monthly — net of<br>commission and refunds, per platform terms.<br><span class="t-val">foodpanda partner terms</span>' },
];
const TICKS: Array<[number, string]> = [[0, 'Fri 8pm'], [1, 'Sat'], [2, 'Sun'], [3, 'Mon'], [4, 'Tue'], [5, 'Wed'], [6.45, '~2 wks']];
const UMAX = 6.8, BREAK = 5.62;
const CHIP_STYLE: Record<Row['chip'], { bg: string; fg: string; label: string }> = {
  GROSS: { bg: '#ecfbf9', fg: '#0d555c', label: 'GROSS' },
  NET: { bg: '#F3EAD3', fg: '#7A5F1D', label: 'NET' },
  CONTRACT: { bg: '#EEF1F2', fg: '#5B6B75', label: 'CONTRACT-BASED' },
};

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'stc-styles';
const CSS = `
.stc-root { --stc-font: 'Poppins', system-ui, -apple-system, sans-serif; --stc-mono: var(--stc-font);
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --gold: #B08A2E; --gold-deep: #7A5F1D;
  position: relative; box-sizing: border-box; width: 100%; max-width: 980px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--stc-font); -webkit-font-smoothing: antialiased; }
.stc-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.stc-root .stc-logo { height: 40px; width: auto; flex: none; }
.stc-root.compact .stc-logo { display: none; }
.stc-root *, .stc-root *::before, .stc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.stc-root h1, .stc-root h2, .stc-root h3, .stc-root p, .stc-root img { margin: 0; }
.stc-root p { letter-spacing: normal; }
.stc-root h1 { font-size: 30px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.25; max-width: 620px; }
.stc-root h1 .gold { color: var(--gold); }
.stc-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 70%; line-height: 1.5; }
.stc-root .chart-wrap { margin-top: 28px; position: relative; }
.stc-root svg.plot { display: block; width: 100%; height: auto; overflow: visible; }
.stc-root .row-name { font-size: 14.5px; font-weight: 600; fill: var(--ink); }
.stc-root .row-sub { font-size: 11px; font-weight: 500; fill: var(--muted); }
.stc-root .form-lbl { font-size: 12px; font-weight: 500; fill: var(--body); }
.stc-root .arrive-lbl { font-size: 12.5px; font-weight: 600; fill: var(--ink-2); }
.stc-root .chip-txt { font-family: var(--stc-mono); font-size: 9.5px; font-weight: 700; letter-spacing: 0.09em; }
.stc-root .axis-lbl { font-family: var(--stc-mono); font-size: 12px; fill: var(--muted); }
.stc-root .band-lbl { font-family: var(--stc-mono); font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; fill: var(--muted); }
.stc-root .anchor-lbl { font-family: var(--stc-mono); font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; fill: var(--teal-deep); }
.stc-root .halo { paint-order: stroke; stroke: #FFFFFF; stroke-width: 4px; stroke-linejoin: round; }
.stc-root .track { stroke: var(--line-2); stroke-width: 1; }
.stc-root .fade { transition: opacity .5s ease; }
.stc-root .hit { cursor: pointer; }
.stc-root .tip { position: absolute; pointer-events: none; background: var(--ink); color: #fff; padding: 9px 12px; border-radius: 10px; font-size: 12.5px; line-height: 1.5; opacity: 0; transform: translate(-50%, calc(-100% - 14px)); transition: opacity .15s; white-space: nowrap; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); z-index: 5; }
.stc-root .tip b { font-weight: 600; }
.stc-root .tip .t-val { color: #A9D9D4; font-family: var(--stc-mono); font-size: 11px; }
.stc-root .tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; transform: translateX(-50%) rotate(45deg); width: 10px; height: 10px; background: var(--ink); }
.stc-root .foot { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--line); }
.stc-root .foot .cap { font-size: 12.5px; line-height: 1.55; color: var(--body); font-style: italic; max-width: 720px; }
.stc-root .src { margin-top: 12px; font-family: var(--stc-mono); font-size: 10px; color: var(--muted); line-height: 1.6; }
.stc-root .src-btn { margin-top: 12px; display: inline-block; background: none; border: none; cursor: pointer; font-family: var(--stc-mono); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--teal-deep); padding: 0; }
.stc-root svg.mob .row-name { font-size: 17px; }
.stc-root svg.mob .row-sub { font-size: 13px; }
.stc-root svg.mob .form-lbl { font-size: 13.5px; }
.stc-root svg.mob .arrive-lbl { font-size: 14px; }
.stc-root svg.mob .chip-txt { font-size: 11px; }
.stc-root svg.mob .axis-lbl { font-size: 13px; }
.stc-root svg.mob .band-lbl { font-size: 11px; }
.stc-root svg.mob .anchor-lbl { font-size: 11px; }
.stc-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.stc-root.compact h1 { font-size: 22px; }
.stc-root.compact .sub { font-size: 13px; max-width: 100%; }
.stc-root.compact .chart-wrap { margin-top: 20px; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
interface Tip { html: string; x: number; y: number; }

const SettlementTimelineChart: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);
  const [compact, setCompact] = React.useState(false);
  const [drawn, setDrawn] = React.useState(false);
  // `tip` keeps the last tooltip's position/content so the fade-out happens in
  // place; clearing it on leave would jump the fading box to the corner.
  const [tip, setTip] = React.useState<Tip | null>(null);
  const [tipOpen, setTipOpen] = React.useState(false);
  const [srcOpen, setSrcOpen] = React.useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setCompact(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  useEffect(() => {
    if (!inView) return;
    const raf = window.requestAnimationFrame(() => setDrawn(false));
    const t = window.setTimeout(() => setDrawn(true), 60);
    return () => { window.cancelAnimationFrame(raf); window.clearTimeout(t); };
  }, [compact, inView]);

  const mob = compact;
  const W = mob ? 540 : 920;
  const rowH = mob ? 92 : 60;
  const M = mob ? { top: 58, right: 22, bottom: 54, left: 22 } : { top: 52, right: 214, bottom: 52, left: 150 };
  const plotW = W - M.left - M.right;
  const rowsH = ROWS.length * rowH + (mob ? 38 : 30);
  const axisY = M.top + rowsH + 10;
  const H = axisY + M.bottom;
  const x = (u: number): number => M.left + (u / UMAX) * plotW;
  const DG = mob ? 38 : 30;
  const rowTop = (i: number): number => M.top + i * rowH + (i === 6 ? DG : 0);
  const rowC = (i: number): number => rowTop(i) + rowH / 2;
  const trackY = (i: number): number => mob ? rowTop(i) + rowH - 24 : rowC(i);
  const x0 = x(0);

  const grow = (i: number): React.CSSProperties => ({
    transformOrigin: `${x0}px 0px`, transform: drawn ? 'scaleX(1)' : 'scaleX(0)',
    transition: `transform .9s cubic-bezier(0.19,1,0.22,1) ${0.15 + i * 0.09}s`,
  });
  const fade = (i: number): React.CSSProperties => ({ opacity: drawn ? 1 : 0, transitionDelay: `${450 + i * 90}ms` });
  const show = (t: Tip): void => { setTip(t); setTipOpen(true); };
  const chipW = (label: string): number => label.length * (mob ? 7.6 : 6.4) + (mob ? 20 : 16);

  return (
    <div className={`stc-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <h1>Six tenders. <span className="gold">Six arrival times.</span></h1>
        <img className="stc-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      </div>
      <p className="sub">When a Friday-night sale actually reaches the bank, by tender type. Same moment of sale — different arrival times, different shapes, different amounts.</p>

      <div className="chart-wrap">
        <svg className={`plot${mob ? ' mob' : ''}`} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Settlement timeline: a Friday 8pm sale arrives via FPS in seconds, cards and Octopus on Monday, AlipayHK and WeChat Pay HK by midweek, and delivery platform payouts roughly two weeks later">
          <rect x={x(1)} y={M.top - 22} width={x(3) - x(1)} height={axisY - M.top + 22} fill="#F5F8F8" />
          <text className="band-lbl" x={(x(1) + x(3)) / 2} y={M.top - 30} textAnchor="middle">Weekend</text>
          <line x1={x0} y1={M.top - 22} x2={x0} y2={axisY} stroke="#0f9993" strokeWidth="1.25" strokeDasharray="3 4" />
          <text className="anchor-lbl" x={x0} y={M.top - 30} textAnchor={mob ? 'start' : 'middle'}>The sale</text>

          {ROWS.map((r, i) => {
            const ty = trackY(i), mx = x(r.u);
            const chip = CHIP_STYLE[r.chip];
            const cw = chipW(chip.label);
            const flip = r.u > 5;
            const alx = flip ? mx - (mob ? 16 : 14) : mx + (mob ? 16 : 14);
            return (
              <g key={r.name}>
                {!mob && i > 0 && <line className="track" x1={M.left - 140} y1={rowTop(i)} x2={W - 20} y2={rowTop(i)} />}
                {mob && i > 0 && <line className="track" x1={M.left} y1={rowTop(i)} x2={W - M.right} y2={rowTop(i)} />}
                {i === 6 && <text className="band-lbl halo" x={mob ? M.left + 12 : x0 - 16} textAnchor={mob ? 'start' : 'end'} y={rowTop(6) - (mob ? 12 : 10)}>the delivery channel</text>}
                {mob ? (
                  <g>
                    <text className="row-name" x={M.left + 12} y={rowTop(i) + 26}>{r.name}{r.sub ? <tspan className="row-sub"> {r.sub}</tspan> : null}</text>
                    <rect x={W - M.right - cw} y={rowTop(i) + 12} width={cw} height={20} rx="10" fill={chip.bg} />
                    <text className="chip-txt" x={W - M.right - cw / 2} y={rowTop(i) + 26} textAnchor="middle" fill={chip.fg}>{chip.label}</text>
                    <text className="form-lbl" x={M.left + 12} y={rowTop(i) + 46} fontStyle={r.hollow ? 'italic' : 'normal'}>{r.form}</text>
                  </g>
                ) : (
                  <g>
                    <text className="row-name" x={M.left - 16} y={rowC(i) - (r.sub ? 2 : -5)} textAnchor="end">{r.name}</text>
                    {r.sub && <text className="row-sub" x={M.left - 16} y={rowC(i) + 14} textAnchor="end">{r.sub}</text>}
                    <text className="form-lbl" x={W - M.right + 18} y={rowC(i) - 3} fontStyle={r.hollow ? 'italic' : 'normal'}>{r.form}</text>
                    <rect x={W - M.right + 18} y={rowC(i) + 3} width={cw} height={17} rx="8.5" fill={chip.bg} />
                    <text className="chip-txt" x={W - M.right + 18 + cw / 2} y={rowC(i) + 15} textAnchor="middle" fill={chip.fg}>{chip.label}</text>
                  </g>
                )}
                <g style={grow(i)}>
                  <line x1={x0} y1={ty} x2={mx} y2={ty} stroke={r.color} strokeWidth={r.hollow ? 1.5 : 2} strokeDasharray={r.hollow ? '2 4' : undefined} opacity={r.hollow ? 0.7 : 1} strokeLinecap="round" />
                </g>
                <g className="fade" style={fade(i)}>
                  {r.range && <line x1={x(r.range[0])} y1={ty} x2={x(r.range[1])} y2={ty} stroke={r.color} strokeWidth="4" opacity="0.18" strokeLinecap="round" />}
                  {r.square
                    ? <rect x={mx - 5.5} y={ty - 5.5} width="11" height="11" rx="2" fill={r.color} stroke="#fff" strokeWidth="2" transform={`rotate(45 ${mx} ${ty})`} />
                    : <circle cx={mx} cy={ty} r={mob ? 7 : 6} fill={r.hollow ? '#fff' : r.color} stroke={r.hollow ? r.color : '#fff'} strokeWidth="2" strokeDasharray={r.hollow ? '2.5 2.5' : undefined} />}
                  <text className="arrive-lbl halo" x={alx} y={ty + (mob ? 5 : 4)} textAnchor={flip ? 'end' : 'start'} fill={r.color === '#B08A2E' ? '#7A5F1D' : undefined}>{r.arrive}</text>
                  {r.range && r.arrive2 && (
                    <g>
                      <circle cx={x(r.range[1])} cy={ty} r={mob ? 7 : 6} fill="#fff" stroke={r.color} strokeWidth="2" strokeDasharray="2.5 2.5" />
                      <text className="arrive-lbl halo" x={x(r.range[1]) + (mob ? 16 : 14)} y={ty + (mob ? 5 : 4)}>{r.arrive2}</text>
                    </g>
                  )}
                </g>
                <circle className="hit" cx={mx} cy={ty} r="16" fill="transparent" onMouseEnter={() => show({ html: r.tip, x: mx, y: ty })} onMouseLeave={() => setTipOpen(false)} onClick={() => show({ html: r.tip, x: mx, y: ty })} />
              </g>
            );
          })}

          <line x1={x0} y1={axisY} x2={x(BREAK)} y2={axisY} stroke="#C7CFD4" strokeWidth="1.25" />
          <line x1={x(BREAK + 0.33)} y1={axisY} x2={x(UMAX)} y2={axisY} stroke="#C7CFD4" strokeWidth="1.25" />
          <line x1={x(BREAK + 0.08)} y1={axisY + 6} x2={x(BREAK + 0.2)} y2={axisY - 6} stroke="#C7CFD4" strokeWidth="1.25" />
          <line x1={x(BREAK + 0.16)} y1={axisY + 6} x2={x(BREAK + 0.28)} y2={axisY - 6} stroke="#C7CFD4" strokeWidth="1.25" />
          {TICKS.map(([u, lbl]) => (
            <g key={lbl}>
              <line x1={x(u)} y1={axisY} x2={x(u)} y2={axisY + 5} stroke="#C7CFD4" strokeWidth="1.25" />
              <text className="axis-lbl" x={x(u)} y={axisY + (mob ? 24 : 22)} textAnchor={u === 0 ? 'start' : u > 6 ? 'end' : 'middle'}>{lbl}</text>
            </g>
          ))}
        </svg>
        <div className="tip" style={tip ? { left: `${(tip.x / W) * 100}%`, top: `${(tip.y / H) * 100}%`, opacity: tipOpen ? 1 : 0 } : undefined} dangerouslySetInnerHTML={{ __html: tip ? tip.html : '' }}></div>
      </div>

      <div className="foot">
        <p className="cap">Same Friday-night sale: FPS is in the account before the customer leaves. Cards and Octopus wait out the weekend and pile up Monday. Delivery money arrives weeks later — net of everything. That&rsquo;s why the POS total never matches the bank.</p>
      </div>
      {compact && !srcOpen
        ? <button className="src-btn" type="button" onClick={() => setSrcOpen(true)}>Click to see sources</button>
        : <p className="src">Sources: HKICL (FPS), Octopus merchant FAQ, AlipayHK merchant FAQ, BOCHK acquiring schedules, Hang Seng merchant services, foodpanda partner terms. Card and WeChat Pay timing vary by acquirer; Octopus T+1 runs from transaction-data upload; delivery payout cycles per platform agreement. Delivery commissions reported at 28–35% (The Collective HK; Unwire, Mar 2025); KeeTa cadence not published.</p>}
    </div>
  );
};

export default SettlementTimelineChart;
